import styled from '@emotion/styled';
import { navigate } from '@reach/router';
import { Button, Heading } from 'components';
import SimplePageLayout, { Subtitle } from 'components/SimplePageLayout';
import { differenceInHours, isValid } from 'date-fns';
import colors from 'lib/colors';
import {
  browserName,
  browserVersion,
  isChrome,
  isEdge,
  isFirefox,
  isIE,
  isMobileOnly,
  isMobileSafari,
  isSafari,
} from 'react-device-detect';
import { Trans, useTranslation } from 'react-i18next';
import { useLocalStorage, useWindowSize } from 'react-use';
import useLogout from 'utils/useLogout';

interface Props {
  children: JSX.Element;
  isOnLoginPage?: boolean;
}

const Compatibility = ({ children, isOnLoginPage }: Props) => {
  const [lowResAcknowledged, setLowResAcknowledged] = useLocalStorage<
    number | null | Boolean
  >('lowResAcknowledged', null);
  let isLowResAcknowledged = false;
  if (isValid(lowResAcknowledged) && typeof lowResAcknowledged === 'number') {
    isLowResAcknowledged =
      differenceInHours(Date.now(), lowResAcknowledged) < 48;
  }

  const { loading: logingOut, logout } = useLogout();
  const { t } = useTranslation();
  const { width, height } = useWindowSize();

  const isResolutionEnough =
    width >= 800 && height >= 600 && height / width <= 0.8;
  const isBrowserSupported = [
    (isChrome || browserName === 'Chrome Headless') &&
      parseInt(browserVersion, 10) >= 60,
    isFirefox && parseInt(browserVersion, 10) >= 66,
    isSafari,
    isMobileSafari,
    isEdge,
    isIE && parseInt(browserVersion, 10) >= 11,
  ].some((pr) => pr);

  if (
    isBrowserSupported &&
    !isMobileOnly &&
    (isResolutionEnough || isLowResAcknowledged)
  ) {
    return children;
  } else if (isMobileOnly) {
    return (
      <NotCompatiblePage
        title={t('mobile.header')}
        subtitle={t('mobile.subtext')}
        acknowledgmentLabel={t('mobile.acknowledgement')}
        acknowledgmentDisabled={logingOut}
        onAcknowledgmentClick={logout}
        isOnLoginPage={isOnLoginPage}
      />
    );
  } else if (!isBrowserSupported) {
    return (
      <NotCompatiblePage
        title={t('notCompatible.header')}
        subtitle={t('notCompatible.subtext')}
        additionalTextKey={'compatibility.text'}
        acknowledgmentLabel={t('notCompatible.homeLink')}
        onAcknowledgmentClick={() =>
          navigate('https://www.clashing.com/', { replace: true })
        }
      />
    );
  } else {
    return (
      <NotCompatiblePage
        title={t('lowResolution.header')}
        subtitle={t('lowResolution.subtext')}
        acknowledgmentLabel={t('lowResolution.acknowledgement')}
        onAcknowledgmentClick={() => setLowResAcknowledged(Date.now())}
      />
    );
  }
};

export default Compatibility;

interface NotCompatiblePageProps {
  title: string;
  subtitle: string;
  additionalTextKey?: string;
  acknowledgmentLabel: string;
  acknowledgmentDisabled?: boolean;
  onAcknowledgmentClick: () => void;
  isOnLoginPage?: boolean;
}
const NotCompatiblePage = ({
  title,
  subtitle,
  additionalTextKey,
  acknowledgmentLabel,
  acknowledgmentDisabled = false,
  onAcknowledgmentClick,
  isOnLoginPage = false,
}: NotCompatiblePageProps) => (
  <SimplePageLayout minHeight={'550px'} languageSwitch>
    <Heading>{title}</Heading>
    <Subtitle>{subtitle}</Subtitle>
    {additionalTextKey && (
      <SmallText>
        <Trans i18nKey={additionalTextKey}></Trans>
      </SmallText>
    )}
    {(!isMobileOnly || !isOnLoginPage) && (
      <CompatibilityButton
        onClick={onAcknowledgmentClick}
        extraWide
        variant="warning"
        disabled={acknowledgmentDisabled}
        label={acknowledgmentLabel}
      />
    )}
  </SimplePageLayout>
);

const SmallText = styled.p`
  font-size: 14px;
  text-align: start;
  letter-spacing: 0;
  line-height: 22px;
  color: ${colors.lobbyText};
  @media screen and (min-width: 1000px) {
    max-width: 40%;
  }
  margin: 16px 0 48px 0;
  @media screen and (max-height: 600px) {
    font-size: 0.7rem;
    line-height: 0.8rem;
    margin: 0.8rem 0 2rem 0;
  }
`;

const CompatibilityButton = styled(Button)({
  '@media screen and (max-height: 600px)': {
    padding: '0.7rem 3rem',
  },
});
